import { useEffect, useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type { DropdownItem } from "@/modules/dropdowns/types";
import type {
  IdeaComment,
  IdeaDetail,
  IdeaListItem,
  IdeaVoteResult,
  NewIdeaInput,
  PaginatedResponse,
  UpdateIdeaInput,
} from "../types";

const IDEAS_KEY = ["ideas"] as const;

export type IdeaListParams = {
  page?: number;
  limit?: number;
  search?: string;
  companyId?: string;
  productId?: string;
  projectId?: string;
  meetingId?: string;
  taskId?: string;
  phaseId?: string;
  stageId?: string;
  status?: string;
  category?: string;
  priority?: string;
  visibility?: string;
  isPinned?: boolean;
  tags?: string[];
};

export function useIdeaList(
  params: IdeaListParams = {},
): UseQueryResult<PaginatedResponse<IdeaListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...IDEAS_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<IdeaListItem>>("/ideas", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          companyId: params.companyId,
          productId: params.productId,
          projectId: params.projectId,
          meetingId: params.meetingId,
          taskId: params.taskId,
          phaseId: params.phaseId,
          stageId: params.stageId,
          status: params.status,
          category: params.category,
          priority: params.priority,
          visibility: params.visibility,
          tags:
            params.tags && params.tags.length > 0
              ? params.tags.join(",")
              : undefined,
          isPinned:
            typeof params.isPinned === "boolean"
              ? params.isPinned
                ? "true"
                : "false"
              : undefined,
        },
      }),
  });
}

export function useIdea(
  id: string | null | undefined,
): UseQueryResult<IdeaDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...IDEAS_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ idea: IdeaDetail }>(`/ideas/${id}`, {
        token: accessToken,
      });
      return res.idea;
    },
  });
}

export function useCreateIdea() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: NewIdeaInput) =>
      apiFetch<{ idea: IdeaDetail }>("/ideas", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY] });
    },
  });
}

export function useUpdateIdea(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateIdeaInput) =>
      apiFetch<{ idea: IdeaDetail }>(`/ideas/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY] });
    },
  });
}

export function useUpdateIdeaStatus(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: string) =>
      apiFetch<{ idea: IdeaDetail }>(`/ideas/${id}/status`, {
        method: "PUT",
        body: { status },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY] });
    },
  });
}

export function useToggleIdeaPin() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    { idea: IdeaDetail },
    Error,
    { id: string; isPinned: boolean }
  >({
    mutationFn: ({ id, isPinned }) =>
      apiFetch<{ idea: IdeaDetail }>(`/ideas/${id}/pin`, {
        method: "PUT",
        body: { isPinned },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY] });
    },
  });
}

export function useDeleteIdea() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/ideas/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY] });
    },
  });
}

export function useVoteIdea(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (voteType: "up" | "down") =>
      apiFetch<IdeaVoteResult>(`/ideas/${id}/vote`, {
        method: "POST",
        body: { voteType },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY, "detail", id] });
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY, "list"] });
    },
  });
}

export function useRemoveIdeaVote(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<IdeaVoteResult>(`/ideas/${id}/vote`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY, "detail", id] });
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY, "list"] });
    },
  });
}

export function useIdeaComments(
  ideaId: string | null | undefined,
): UseQueryResult<IdeaComment[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...IDEAS_KEY, "comments", ideaId, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!ideaId,
    queryFn: async () => {
      const res = await apiFetch<{ data: IdeaComment[] }>(
        `/ideas/${ideaId}/comments`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function useAddIdeaComment(ideaId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiFetch<{ comment: IdeaComment }>(`/ideas/${ideaId}/comments`, {
        method: "POST",
        body: { content },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY, "comments", ideaId] });
    },
  });
}

export function useDeleteIdeaComment(ideaId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiFetch(`/ideas/${ideaId}/comments/${commentId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY, "comments", ideaId] });
    },
  });
}

export function useLinkDecision(ideaId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (decisionId: string) =>
      apiFetch<{ success: true }>(`/ideas/${ideaId}/link-decision`, {
        method: "POST",
        body: { decisionId },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY, "detail", ideaId] });
    },
  });
}

export function useUnlinkDecision(ideaId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (decisionId: string) =>
      apiFetch<{ success: true }>(
        `/ideas/${ideaId}/link-decision/${decisionId}`,
        {
          method: "DELETE",
          token: accessToken,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...IDEAS_KEY, "detail", ideaId] });
    },
  });
}

export function useIdeaCategories(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("idea_category");
}

export function useIdeaPriorities(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("idea_priority");
}

export function useIdeaStatuses(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("idea_status");
}

export function useIdeaTags(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("idea_tag");
}

type AutoSaveState = {
  isSaving: boolean;
  lastSaved: Date | null;
  isDirty: boolean;
};

export function useAutoSaveIdea(
  ideaId: string,
  title: string,
  content: string,
  enabled: boolean,
): AutoSaveState & { saveNow: () => Promise<void> } {
  const update = useUpdateIdea(ideaId);
  const [state, setState] = useState<AutoSaveState>({
    isSaving: false,
    lastSaved: null,
    isDirty: false,
  });
  const initial = useRef<{ title: string; content: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (initial.current === null) {
      initial.current = { title, content };
      return;
    }
    const dirty =
      title !== initial.current.title || content !== initial.current.content;
    setState((s) => ({ ...s, isDirty: dirty }));
    if (!dirty) return;

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void doSave();
    }, 2000);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, content, enabled]);

  useEffect(() => {
    if (!enabled) {
      initial.current = null;
      setState({ isSaving: false, lastSaved: null, isDirty: false });
    }
  }, [enabled]);

  async function doSave() {
    if (!ideaId) return;
    setState((s) => ({ ...s, isSaving: true }));
    try {
      await update.mutateAsync({ title, content });
      initial.current = { title, content };
      setState({ isSaving: false, lastSaved: new Date(), isDirty: false });
    } catch {
      setState((s) => ({ ...s, isSaving: false }));
    }
  }

  async function saveNow() {
    if (timer.current) clearTimeout(timer.current);
    await doSave();
  }

  return { ...state, saveNow };
}
